import QnAddReaction from "@assets/quick-news/likeIcon.svg";
import IcAddReaction from "@assets/reactions/add-reactions.svg";
import { reactionCount } from "@helper/utils";
import type { ReactionProp } from "@typings/Reactions";
import type { ReactElement } from "react";
import { twMerge } from "tailwind-merge";

import { getReactionGif } from "./helper/getReactionGif";

export type ArticleReactionsProps = {
  reactions: ReactionProp[];
  reactionsUser: string;
  limit?: 1 | 2;
  referer?: string;
  variant?: "default" | "small";
  qnStyle?: boolean;
};

const NO_REACTION_USER_ID = "0";

export const ArticleReactions = ({
  reactions,
  reactionsUser,
  limit = 2,
  referer = "article",
  variant = "default",
  qnStyle = false,
}: ArticleReactionsProps): ReactElement => {
  const hasSelectedReaction =
    Boolean(reactionsUser) && reactionsUser !== NO_REACTION_USER_ID;

  if (reactions.length === 0) {
    return <span key={`reaction-empty-${referer}`} data-testid="no-reaction" />;
  }

  const totalReactions = reactions.reduce(
    (sum, reaction) => sum + reaction.count,
    0,
  );

  if (totalReactions === 0) {
    const addReactionClassName = twMerge(
      "w-[40px] flex items-center gap-0.5 rounded-[50px] py-2 px-2",
      !qnStyle && "border border-red-700",
      variant === "small" && "h-8",
    );

    return (
      <div className={addReactionClassName}>
        <img
          width={24}
          height={24}
          src={qnStyle ? QnAddReaction : IcAddReaction}
          data-testid="add-reaction"
          alt="add-reaction"
        />
      </div>
    );
  }

  const topReactions = [...reactions]
    .filter((reaction) => reaction.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);

  const reactionsContainerClassName = twMerge(
    "w-fit flex items-center justify-center gap-0.5 rounded-[50px] py-2.5 px-3",
    !qnStyle && "border",
    !qnStyle && (hasSelectedReaction ? "border-red-700" : "border-grey-350"),
    variant === "small" && "h-8",
  );

  const countClassName = twMerge(
    "text-sm font-semimedium leading-[150%]",
    hasSelectedReaction && "text-grey-500",
  );

  return (
    <div className={reactionsContainerClassName}>
      <div className="flex items-center">
        {topReactions.map((reaction, index) => (
          <span
            key={`reactions-span-${referer}-${reaction.id}`}
            data-testid={`reaction-${index}`}
            className={index > 0 ? "-ml-1" : ""}
          >
            <img
              src={getReactionGif(reaction.id)}
              alt={reaction.text}
              width={20}
            />
          </span>
        ))}
      </div>
      <span className={countClassName}>{reactionCount(totalReactions)}</span>
    </div>
  );
};
